// src/pages/NfcCardEditor.js
//
// Rep-side editor for the NFC business card.
// Form fields: photo, displayName, slug, headline, company, whatsappNumber,
// email, isPublished. Shows the public URL with a Copy button so the rep can
// paste it into their external NFC writer program.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { colors } from '../theme/tokens';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

const NfcCardEditor = () => {
  const { token, canAccessNfc, user } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoSrc, setPhotoSrc] = useState(null);
  const photoObjectUrlRef = useRef(null);

  // The "card" state mirrors what the server gives us back. We dirty-track
  // separately so the Publish toggle can update independently of typing.
  const [card, setCard] = useState({
    slug: '',
    displayName: '',
    headline: '',
    company: '',
    whatsappNumber: '',
    email: '',
    hasPhoto: false,
    isPublished: false,
    updatedAt: null
  });

  // Whether the user has ever saved a slug. Until they have, we keep the slug
  // input tracking displayName automatically (only if they haven't manually
  // edited the slug yet).
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (!canAccessNfc) {
      // Don't kick the user out — they may have just had access revoked.
      // Just stop loading so the gate message renders.
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/nfc-cards/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCard({
          slug: data.slug || '',
          displayName: data.displayName || '',
          headline: data.headline || '',
          company: data.company || '',
          whatsappNumber: data.whatsappNumber || '',
          email: data.email || '',
          hasPhoto: !!data.hasPhoto,
          isPublished: !!data.isPublished,
          updatedAt: data.updatedAt || null
        });
        if (data.slug) setSlugTouched(true);
      } catch (err) {
        console.error('Failed to load NFC card:', err);
        showToast('Failed to load your card', 'error');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessNfc, token]);

  // Photo preview: the editor needs to show the photo BEFORE the card is
  // published, so we fetch the authed /me/photo endpoint as a blob and turn
  // it into an object URL. (<img src> can't send Authorization headers.)
  useEffect(() => {
    let cancelled = false;
    // Reset any previously held object URL.
    if (photoObjectUrlRef.current) {
      URL.revokeObjectURL(photoObjectUrlRef.current);
      photoObjectUrlRef.current = null;
    }
    if (!card.hasPhoto || !token) {
      setPhotoSrc(null);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/nfc-cards/me/photo`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        photoObjectUrlRef.current = url;
        setPhotoSrc(url);
      } catch (err) {
        if (!cancelled) setPhotoSrc(null);
      }
    })();
    return () => { cancelled = true; };
  }, [card.hasPhoto, card.updatedAt, token]);

  useEffect(() => {
    return () => {
      if (photoObjectUrlRef.current) {
        URL.revokeObjectURL(photoObjectUrlRef.current);
      }
    };
  }, []);

  // Auto-track slug from displayName until the user touches the slug field.
  const setField = (key, value) => {
    setCard((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'displayName' && !slugTouched) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const publicUrl = useMemo(() => {
    if (!card.slug) return '';
    return `${window.location.origin}/card/${card.slug}`;
  }, [card.slug]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        slug: card.slug || undefined,
        displayName: card.displayName,
        headline: card.headline,
        company: card.company,
        whatsappNumber: card.whatsappNumber,
        email: card.email,
        isPublished: card.isPublished
      };
      const { data } = await axios.put(`${API_URL}/api/nfc-cards/me`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCard((prev) => ({
        ...prev,
        slug: data.slug,
        displayName: data.displayName,
        headline: data.headline,
        company: data.company,
        whatsappNumber: data.whatsappNumber,
        email: data.email,
        isPublished: !!data.isPublished,
        updatedAt: data.updatedAt || prev.updatedAt
      }));
      setSlugTouched(true);
      showToast('Card saved', 'success');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save card';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast('Photo must be under 5 MB', 'error');
      return;
    }
    const form = new FormData();
    form.append('photo', file);
    setUploadingPhoto(true);
    try {
      await axios.post(`${API_URL}/api/nfc-cards/me/photo`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      // Bust browser cache on the same slug by appending a query string.
      setCard((prev) => ({ ...prev, hasPhoto: true, updatedAt: new Date().toISOString() }));
      showToast('Photo updated', 'success');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to upload photo';
      showToast(msg, 'error');
    } finally {
      setUploadingPhoto(false);
      // Reset the input so picking the same file again still triggers onChange.
      e.target.value = '';
    }
  };

  const handlePhotoRemove = async () => {
    setUploadingPhoto(true);
    try {
      await axios.delete(`${API_URL}/api/nfc-cards/me/photo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCard((prev) => ({ ...prev, hasPhoto: false }));
      showToast('Photo removed', 'info');
    } catch (err) {
      showToast('Failed to remove photo', 'error');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      showToast('URL copied to clipboard', 'success');
    } catch (_) {
      showToast('Copy failed — long-press the URL to copy manually', 'warning');
    }
  };

  if (loading) {
    return <div style={styles.loader}>Loading your card…</div>;
  }

  if (!canAccessNfc) {
    return (
      <div style={styles.page}>
        <div style={styles.gateCard}>
          <h2 style={{ marginTop: 0 }}>NFC Business Card</h2>
          <p>
            You don't have NFC business card access yet. Ask a Rubber-tier admin
            to enable it for your account from the Admin → Users panel.
          </p>
          <button onClick={() => navigate('/')} style={styles.linkBtn}>
            Back to home
          </button>
        </div>
      </div>
    );
  }

  // Show the "not published" callout once they have a slug saved server-side
  // (so they actually have a URL to share) but isPublished is still false.
  const showUnpublishedWarning = !!card.slug && !card.isPublished;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📇 NFC Business Card</h1>
          <p style={styles.subtitle}>
            Set up your public business card. Once you publish, paste the URL below
            into any NFC writer program (e.g. <em>NFC Tools</em>) to program your card.
          </p>
        </div>
      </div>

      {showUnpublishedWarning && (
        <div style={styles.warningBanner}>
          <strong>Your card isn't published yet.</strong> Tick <em>Published</em> below
          and press <em>Save card</em> — until then, your public URL will return
          "Card not found".
        </div>
      )}

      <div className="nfc-editor-grid" style={styles.grid}>
        {/* ── Editor column ───────────────────────────────────────────── */}
        <div style={styles.editorCol}>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Profile photo</h3>
            <div style={styles.photoRow}>
              <div style={styles.avatarWrap}>
                {photoSrc ? (
                  <img src={photoSrc} alt="Profile" style={styles.avatar} />
                ) : (
                  <div style={styles.avatarPlaceholder}>No photo</div>
                )}
              </div>
              <div style={styles.photoActions}>
                <label style={styles.fileBtn}>
                  {uploadingPhoto ? 'Uploading…' : (card.hasPhoto ? 'Replace photo' : 'Upload photo')}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoChange}
                    disabled={uploadingPhoto}
                    style={{ display: 'none' }}
                  />
                </label>
                {card.hasPhoto && (
                  <button
                    type="button"
                    onClick={handlePhotoRemove}
                    disabled={uploadingPhoto}
                    style={styles.dangerBtn}
                  >
                    Remove
                  </button>
                )}
                <div style={styles.hint}>JPG, PNG or WebP, up to 5 MB.</div>
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Card details</h3>

            <Field label="Display name">
              <input
                type="text"
                value={card.displayName}
                onChange={(e) => setField('displayName', e.target.value)}
                placeholder={user?.name || 'Your full name'}
                maxLength={80}
                style={styles.input}
              />
            </Field>

            <Field label="Card URL slug" hint="Used in your public URL. Lowercase letters, numbers, dashes.">
              <div style={styles.slugPrefixStandalone}>
                {window.location.origin}/card/<strong>{card.slug || '…'}</strong>
              </div>
              <input
                type="text"
                value={card.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setField('slug', slugify(e.target.value));
                }}
                placeholder="johann-de-beer"
                maxLength={40}
                style={styles.input}
              />
            </Field>

            <Field label="Headline / role" hint="Optional, shown under your name.">
              <input
                type="text"
                value={card.headline}
                onChange={(e) => setField('headline', e.target.value)}
                placeholder="Sales Rep – Western Cape"
                maxLength={120}
                style={styles.input}
              />
            </Field>

            <Field label="Company">
              <input
                type="text"
                value={card.company}
                onChange={(e) => setField('company', e.target.value)}
                placeholder="Par Mega Store"
                maxLength={80}
                style={styles.input}
              />
            </Field>

            <Field
              label="WhatsApp number"
              hint={
                card.whatsappNumber && card.whatsappNumber.startsWith('0')
                  ? '⚠ Looks like a local number — drop the leading 0 and start with your country code (e.g. 27 for South Africa).'
                  : 'Type your country code + number. The leading + is added automatically.'
              }
            >
              <div style={styles.phoneRow}>
                <span style={styles.phonePrefix}>+</span>
                <input
                  type="tel"
                  value={card.whatsappNumber}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D+/g, '').slice(0, 15);
                    setField('whatsappNumber', digits);
                  }}
                  placeholder="27821234567"
                  inputMode="numeric"
                  maxLength={15}
                  style={{ ...styles.input, flex: 1, borderRadius: '0 6px 6px 0' }}
                />
              </div>
            </Field>

            <Field label="Email" hint="Optional — adds a second contact button to your card.">
              <input
                type="email"
                value={card.email}
                onChange={(e) => setField('email', e.target.value)}
                placeholder="johann@example.com"
                style={styles.input}
              />
            </Field>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Visibility</h3>
            <label style={styles.toggleRow}>
              <input
                type="checkbox"
                checked={card.isPublished}
                onChange={(e) => setField('isPublished', e.target.checked)}
              />
              <span>
                <strong>{card.isPublished ? 'Published' : 'Unpublished'}</strong>
                <div style={styles.hint}>
                  When unpublished, your public URL returns "Card not found".
                </div>
              </span>
            </label>
          </div>

          <div style={styles.saveRow}>
            <button onClick={handleSave} disabled={saving} style={styles.primaryBtn}>
              {saving ? 'Saving…' : 'Save card'}
            </button>
          </div>
        </div>

        {/* ── Preview / URL column ────────────────────────────────────── */}
        <div style={styles.sideCol}>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Your public URL</h3>
            {card.slug ? (
              <>
                <div style={styles.urlBox}>{publicUrl}</div>
                <div style={styles.urlActions}>
                  <button onClick={handleCopyUrl} style={styles.primaryBtn}>Copy URL</button>
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.secondaryBtn}
                  >
                    Open
                  </a>
                </div>
                <p style={styles.hint}>
                  Paste this URL into any NFC writer program (e.g. <em>NFC Tools</em> on
                  Android/iPhone) to program your physical NFC card.
                </p>
              </>
            ) : (
              <div style={styles.hint}>
                Save your card to generate a public URL.
              </div>
            )}
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>How it works</h3>
            <ol style={styles.steps}>
              <li>Fill in your details and press <strong>Save card</strong>.</li>
              <li>Tick <strong>Published</strong> and save again.</li>
              <li>Copy the URL above and write it to your NFC card using <strong>NFC Tools</strong>.</li>
              <li>
                When a client taps their phone on your card, your page opens. They fill in
                their name and company, then tap <strong>WhatsApp</strong> — your phone gets a
                message pre-filled with their details, so you know exactly who reached out.
              </li>
            </ol>

            <div style={styles.appBox}>
              <div style={styles.appBoxTitle}>📱 Android</div>
              <p style={styles.appBoxText}>
                Install <strong>NFC Tools</strong>, open it, tap <em>Write → Add a record → URL/URI</em>,
                paste your card URL, then hold your phone against the NFC card.
              </p>
              <a
                href="https://play.google.com/store/apps/details?id=com.wakdev.wdnfc"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.playStoreBtn}
              >
                Get NFC Tools on Google Play
              </a>
            </div>

            <div style={styles.appBox}>
              <div style={styles.appBoxTitle}>🍎 iPhone</div>
              <p style={styles.appBoxText}>
                iPhones can read NFC cards but can't reliably <em>write</em> to them.
                Ask a friend with an Android phone to install NFC Tools (link above) and
                program your card for you — it only takes a minute.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div style={styles.field}>
    <label style={styles.label}>{label}</label>
    {children}
    {hint && <div style={styles.hint}>{hint}</div>}
  </div>
);

const styles = {
  page: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '20px 14px 60px',
    boxSizing: 'border-box',
    width: '100%'
  },
  loader: {
    textAlign: 'center',
    padding: '60px',
    color: colors.textMuted
  },
  gateCard: {
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '12px',
    padding: '32px',
    maxWidth: '520px',
    margin: '60px auto',
    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
    color: colors.text
  },
  header: {
    marginBottom: '24px'
  },
  title: {
    margin: '0 0 4px',
    fontSize: '24px',
    color: colors.text
  },
  subtitle: {
    margin: 0,
    color: colors.textMuted,
    fontSize: '14px'
  },
  warningBanner: {
    background: 'rgba(240,168,48,0.12)',
    border: '1px solid rgba(240,168,48,0.4)',
    color: colors.text,
    padding: '12px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '16px',
    lineHeight: 1.5
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)',
    gap: '20px',
    alignItems: 'start'
  },
  editorCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: 0
  },
  sideCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: 0
  },
  section: {
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '10px',
    padding: '18px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
    color: colors.text
  },
  sectionTitle: {
    margin: '0 0 12px',
    fontSize: '15px',
    color: colors.text,
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  photoRow: {
    display: 'flex',
    gap: '14px',
    alignItems: 'flex-start',
    flexWrap: 'wrap'
  },
  avatarWrap: {
    width: '96px',
    height: '96px',
    flexShrink: 0,
    borderRadius: '50%',
    overflow: 'hidden',
    background: colors.surface,
    border: `2px solid ${colors.border}`
  },
  avatar: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.textMuted,
    fontSize: '12px'
  },
  photoActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
    minWidth: '180px'
  },
  fileBtn: {
    background: '#2fb65c',
    color: 'white',
    padding: '8px 14px',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '13px',
    cursor: 'pointer',
    textAlign: 'center',
    display: 'inline-block'
  },
  dangerBtn: {
    background: 'rgba(231,76,60,0.10)',
    color: colors.red,
    border: '1px solid rgba(231,76,60,0.45)',
    padding: '6px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600
  },
  field: {
    marginBottom: '12px'
  },
  label: {
    display: 'block',
    marginBottom: '4px',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.text
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
    background: colors.inputBg,
    color: colors.text
  },
  slugRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap'
  },
  slugPrefix: {
    color: colors.textMuted,
    fontSize: '12px',
    whiteSpace: 'nowrap'
  },
  slugPrefixStandalone: {
    color: colors.textMuted,
    fontSize: '12px',
    marginBottom: '6px',
    overflowWrap: 'anywhere',
    background: colors.surfaceSoft,
    padding: '6px 8px',
    borderRadius: '4px',
    fontFamily: 'monospace'
  },
  phoneRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '0'
  },
  phonePrefix: {
    background: colors.surface2,
    border: `1px solid ${colors.border}`,
    borderRight: 'none',
    borderRadius: '6px 0 0 6px',
    padding: '8px 10px',
    color: colors.textMuted,
    fontWeight: 700,
    fontSize: '15px',
    display: 'flex',
    alignItems: 'center'
  },
  hint: {
    fontSize: '12px',
    color: colors.textMuted,
    marginTop: '4px'
  },
  toggleRow: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
    cursor: 'pointer'
  },
  saveRow: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  primaryBtn: {
    background: '#2fb65c',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer'
  },
  secondaryBtn: {
    background: colors.surface,
    color: colors.text,
    border: `1px solid ${colors.glassBorderStrong}`,
    padding: '10px 20px',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    textAlign: 'center'
  },
  linkBtn: {
    background: 'transparent',
    color: colors.parGreenBright,
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    padding: 0
  },
  urlBox: {
    background: colors.surfaceSoft,
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    padding: '10px 12px',
    fontFamily: 'monospace',
    fontSize: '13px',
    color: colors.text,
    overflowWrap: 'anywhere',
    marginBottom: '10px'
  },
  urlActions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap'
  },
  steps: {
    margin: 0,
    paddingLeft: '20px',
    color: colors.textMuted,
    fontSize: '13px',
    lineHeight: 1.6
  },
  appBox: {
    marginTop: '14px',
    padding: '12px 14px',
    background: colors.surfaceSoft,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '8px'
  },
  appBoxTitle: {
    fontWeight: 700,
    fontSize: '13px',
    color: colors.text,
    marginBottom: '6px'
  },
  appBoxText: {
    margin: '0 0 10px',
    fontSize: '13px',
    color: colors.textMuted,
    lineHeight: 1.5
  },
  playStoreBtn: {
    display: 'inline-block',
    background: colors.surface2,
    border: `1px solid ${colors.glassBorderStrong}`,
    color: colors.text,
    padding: '10px 14px',
    borderRadius: '6px',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '13px'
  }
};

export default NfcCardEditor;
